import type { GridApi } from 'ag-grid-community'
import { IDatasource, IGetRowsParams } from 'ag-grid-community'
import { client } from '@/api/backoffice-api'
import AppConfig from '@/utils/app-config.mjs'
import logger from '@/utils/logger.mjs'

export const EVENT_LOGS_PAGE_SIZE = 20

export interface EventLogsFilterParams {
  transfer_id?: string
  order_number?: string
  store_id?: string
  dealerId?: string
  action?: string
  showHidden?: boolean
  search?: string
  date_from?: string
  date_to?: string
}

export interface EventLogRow {
  id?: number
  transferId: string
  orderNumber: string
  time: string
  updatedBy: string
  action: 'Called' | 'Emailed' | 'Profile Edit' | 'System' | 'Other'
  notes: string
  store: string
  isHidden?: boolean
}

interface EventLogsApiRow {
  id?: number
  transfer_id?: string | number
  order_number?: string
  created_at?: string
  updated_at?: string
  createdAt?: string
  created_by?: string
  updated_by?: string
  action?: string
  notes?: string
  store_name?: string
  store_id?: string | null
  is_hidden?: boolean
  is_hidden_by?: string | null
  is_hidden_at?: string | null
}

interface EventLogsResponse {
  data?: EventLogsApiRow[]
  pagination?: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
  total?: number
  totalCount?: number
  total_count?: number
  meta?: {
    total?: number
    totalCount?: number
    total_count?: number
  }
}

export interface CreateEventLogsDataSourceOptions {
  getFilters: () => EventLogsFilterParams
  onTotal?: (total: number) => void
  /** Called when a fetch starts; startRow 0 = initial load, >0 = pagination. */
  onFetchStart?: (startRow: number) => void
  /** Called when a fetch ends (success or failure). */
  onFetchEnd?: () => void
  /** Grid API for no-rows overlay and clearing overlay. */
  getApi?: () => GridApi | undefined
}

const EMPTY_PLACEHOLDER = '—'

const VALID_ACTIONS: EventLogRow['action'][] = [
  'Called',
  'Emailed',
  'Profile Edit',
  'System',
  'Other',
]

function toDisplay(value: string | null | undefined): string {
  const s = value != null ? String(value).trim() : ''
  return s || EMPTY_PLACEHOLDER
}

function toTitleCase(s: string): string {
  return s
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function mapRow(row: EventLogsApiRow): EventLogRow {
  const time = toDisplay(row.createdAt ?? row.created_at ?? '')
  const actionRaw = row.action ?? 'Other'
  const actionTitle = toTitleCase(actionRaw.trim()) as EventLogRow['action']
  const action: EventLogRow['action'] = VALID_ACTIONS.includes(actionTitle) ? actionTitle : 'Other'
  const transferId = row.transfer_id != null ? String(row.transfer_id).trim() : ''
  return {
    id: row.id,
    transferId, // keep raw so Transfer link uses real id when present
    orderNumber: toDisplay(row.order_number),
    time,
    updatedBy: toDisplay(row.created_by ?? row.updated_by),
    action,
    notes: toDisplay(row.notes),
    store: toDisplay(row.store_name),
    isHidden: row.is_hidden ?? false,
  }
}

export default class EventLogsDataSource implements IDatasource {
  private getFilters: () => EventLogsFilterParams
  private onTotal?: (total: number) => void
  private onFetchStart?: (startRow: number) => void
  private onFetchEnd?: () => void
  private getApi?: () => GridApi | undefined

  constructor(options: CreateEventLogsDataSourceOptions) {
    this.getFilters = options.getFilters
    this.onTotal = options.onTotal
    this.onFetchStart = options.onFetchStart
    this.onFetchEnd = options.onFetchEnd
    this.getApi = options.getApi
  }

  async fetchData(params: {
    filters: EventLogsFilterParams
    page: number
    pageSize: number
  }): Promise<EventLogsResponse> {
    const { filters, page, pageSize } = params
    const query = new URLSearchParams()
    if (filters.transfer_id) query.set('transfer_id', filters.transfer_id)
    if (filters.order_number) query.set('order_number', filters.order_number)
    if (filters.store_id) query.set('store_id', filters.store_id)
    if (filters.dealerId) query.set('dealer_id', filters.dealerId)
    if (filters.action) query.set('action', filters.action)
    if (filters.showHidden !== undefined) query.set('showHidden', String(filters.showHidden))
    if (filters.search) query.set('search', filters.search)
    if (filters.date_from) query.set('dateFrom', filters.date_from)
    if (filters.date_to) query.set('dateTo', filters.date_to)
    query.set('page', String(page))
    query.set('pageSize', String(pageSize))

    const response = await client.get(
      `${AppConfig.fflExpressServiceApiURL}/backend/transfers/event-logs?${query.toString()}`,
      { timeout: 60000 }
    )
    return response.data as EventLogsResponse
  }

  getRows(params: IGetRowsParams): void {
    const startRow = params.startRow ?? 0
    const page = Math.floor(startRow / EVENT_LOGS_PAGE_SIZE) + 1
    const filters = this.getFilters()

    this.onFetchStart?.(startRow)

    this.fetchData({ filters, page, pageSize: EVENT_LOGS_PAGE_SIZE })
      .then((result) => {
        const rows = (result.data ?? []).map(mapRow)
        const total =
          result.pagination?.total ??
          result.total ??
          result.totalCount ??
          result.total_count ??
          result.meta?.total ??
          result.meta?.totalCount ??
          result.meta?.total_count
        if (total != null && this.onTotal) this.onTotal(Number(total))
        const lastRow = total != null && startRow + rows.length >= total ? total : undefined

        if (startRow === 0) {
          if (rows.length === 0) this.getApi?.()?.showNoRowsOverlay?.()
          else this.getApi?.()?.hideOverlay?.()
        }
        params.successCallback(rows, lastRow)
      })
      .catch((err: unknown) => {
        logger.error('Event logs fetch error', err)
        if (startRow === 0) this.getApi?.()?.hideOverlay?.()
        params.failCallback()
      })
      .finally(() => {
        this.onFetchEnd?.()
      })
  }
}
